import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import type { OverheadSubcategoryItem } from "@/hooks/use-overhead-categories";
import {
  useAddOverheadSubcategory,
  useUpdateOverheadSubcategoryName,
} from "@/hooks/use-overhead-categories";
import type {
  AddOverheadSubcategoryInput,
  UpdateOverheadSubcategoryInput,
} from "@/schemas/overhead-categories";
import {
  AddOverheadSubcategorySchema,
  UpdateOverheadSubcategorySchema,
} from "@/schemas/overhead-categories";

// ── New Subcategory ────────────────────────────────────────────────────────────

interface NewOverheadSubcategoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  categoryId: string;
  categoryCode: string;
}

export function NewOverheadSubcategoryDialog({
  open,
  onOpenChange,
  categoryId,
  categoryCode,
}: NewOverheadSubcategoryDialogProps) {
  const addSubcategory = useAddOverheadSubcategory();
  const form = useForm<AddOverheadSubcategoryInput>({
    resolver: zodResolver(AddOverheadSubcategorySchema),
    defaultValues: { name: "" },
  });

  useEffect(() => {
    if (!open) form.reset();
  }, [open, form]);

  function onSubmit(values: AddOverheadSubcategoryInput) {
    addSubcategory.mutate(
      { categoryId, ...values },
      {
        onSuccess: () => {
          form.reset();
          onOpenChange(false);
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add Subcategory</DialogTitle>
          <DialogDescription>
            Add a subcategory to <strong className="text-foreground">{categoryCode}</strong>.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g. Training" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={addSubcategory.isPending}>
                {addSubcategory.isPending ? "Adding..." : "Add"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

// ── Edit Subcategory ───────────────────────────────────────────────────────────

interface EditOverheadSubcategoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  subcategory: OverheadSubcategoryItem;
}

export function EditOverheadSubcategoryDialog({
  open,
  onOpenChange,
  subcategory,
}: EditOverheadSubcategoryDialogProps) {
  const updateSubcategory = useUpdateOverheadSubcategoryName();
  const form = useForm<UpdateOverheadSubcategoryInput>({
    resolver: zodResolver(UpdateOverheadSubcategorySchema),
    defaultValues: { name: subcategory.name },
  });

  useEffect(() => {
    if (open) form.reset({ name: subcategory.name });
  }, [open, subcategory, form]);

  function onSubmit(values: UpdateOverheadSubcategoryInput) {
    updateSubcategory.mutate(
      { id: subcategory.id, ...values },
      { onSuccess: () => onOpenChange(false) },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Subcategory</DialogTitle>
          <DialogDescription>Update the subcategory name.</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={updateSubcategory.isPending}>
                {updateSubcategory.isPending ? "Saving..." : "Save"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
